"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlockButton = exports.AltButton = exports.Button = void 0;
const React = require("react");
const react_router_dom_1 = require("react-router-dom");
const Modal_1 = require("../../structure/Modal");
const colors_1 = require("../../../../types/colors");
const Badge_1 = require("../Badge");
const styles = require("./styles.scss");
const cssComposer_1 = require("../../../../helpers/cssComposer");
const dialogs_1 = require("../../../../types/dialogs");
const isPromise_1 = require("../../../../helpers/isPromise");
const Icon_1 = require("../Icon");
const onClickStopPropagation_1 = require("../../../../helpers/onClickStopPropagation");
const css = (0, cssComposer_1.default)(styles, "Button");
const MODES = {
    READY: "default",
    PENDING: "pending",
    SUCCESS: "success",
    ERROR: "error"
};
/**
 * A button that supports asyncronous requests. To utilise, the onClick method
 * needs to pass a promise on click.
 */
class Button extends React.Component {
    constructor(props) {
        super(props);
        this.timeout = null;
        this.buttonRef = null;
        this._isMounted = false;
        this.state = {
            mode: MODES.READY,
            askForConfirmation: false
        };
        this.buttonRef = React.createRef();
    }
    componentDidMount() {
        this._isMounted = true;
    }
    componentWillUnmount() {
        this._isMounted = false;
        // clear any timeouts pending
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }
    async promiseHandler(promise) {
        // since this is an async function and our button component might be unmounted at any time,
        // we execute each async step only when the component is still mounted to prevent memory leak.
        try {
            this._isMounted && this.setState({ mode: MODES.PENDING });
            if (this._isMounted) {
                const result = await promise;
                result && this._isMounted && this.setState({ mode: MODES.SUCCESS });
            }
        }
        catch (e) {
            this._isMounted && this.setState({ mode: MODES.ERROR });
            // we've handled the error, throw it so it can be handled/logged at a higher level
            throw e;
        }
        finally {
            const { disableOnError = false, disableOnSuccess = true } = this.props;
            // wait for the state to be updated
            this._isMounted &&
                setTimeout(() => {
                    // don't allow it to be resubmitted
                    if (disableOnSuccess && this.state.mode === MODES.SUCCESS) {
                        return;
                    }
                    if (disableOnError && this.state.mode === MODES.ERROR) {
                        return;
                    }
                    // wait a brief moment before returning to a reusable state
                    if (this._isMounted) {
                        this.timeout = setTimeout(() => {
                            clearTimeout(this.timeout);
                            this._isMounted && this.setState({ mode: MODES.READY });
                        }, 1500); // node Timer typedef is bad
                    }
                });
        }
    }
    render() {
        const { mode } = this.state;
        let { variant } = this.props;
        const { children, onClick, to, type = "button", // HACK - remove support in the future
        isBlock = false, disabled = false, className = "", shouldConfirm, status, badge = null, isActive = false, forceLink = false, icon, target } = this.props;
        // allow falsy values to be passed, e.g. null
        variant = variant || "primary";
        // ensure a known value
        const size = this.props.size || "md";
        const classNames = [
            css("", `-variant-${variant}`, `-status-${status}`, `-size-${size}`, {
                "-pending": mode === MODES.PENDING,
                "-block": isBlock,
                "-error": mode === MODES.ERROR,
                "-success": mode === MODES.SUCCESS,
                "-hasBadge": badge !== null,
                "-isActive": isActive
            }),
            className
        ].join(" ");
        const onConfirmationRequiredClick = () => {
            this.setState({ askForConfirmation: true });
        };
        const onButtonClick = (event) => {
            // Hide confirmation modal
            if (this.state.askForConfirmation) {
                this.setState({ askForConfirmation: false });
            }
            if (!onClick) {
                return;
            }
            // stop event from bubbling up
            if (event) {
                event.stopPropagation();
            }
            // don't allow multiple executions
            if (this.state.mode !== MODES.READY) {
                return;
            }
            // pass the event to the callback in case it's needed
            const result = onClick(event);
            if ((0, isPromise_1.isPromise)(result)) {
                return this.promiseHandler(result);
            }
            return result;
        };
        if (to) {
            if (onClick) {
                throw new Error("Cannot provide both 'onClick' & 'to' props to Button component.");
            }
            // Need to prevent click events from propagating.
            const stopClickEvents = (0, onClickStopPropagation_1.onClickStopPropagation)(() => {
                return;
            });
            if (env.webLinks || forceLink) {
                return (React.createElement("a", { href: to, onClick: stopClickEvents, className: classNames, target: target },
                    React.createElement("span", null, children)));
            }
            else {
                return (React.createElement(react_router_dom_1.NavLink, { to: to, onClick: stopClickEvents, className: classNames, "data-test": "button" },
                    React.createElement("span", null, children)));
            }
        }
        const dismissConfirmation = () => this.setState({ askForConfirmation: false });
        return (React.createElement(React.Fragment, null,
            React.createElement("button", { ref: this.buttonRef, className: classNames, onClick: shouldConfirm ? onConfirmationRequiredClick : onButtonClick, type: type, disabled: disabled || !onClick, "data-test": "button", "data-test-is-block": isBlock, "data-test-mode": mode },
                !!icon && React.createElement(Icon_1.Icon, { name: icon, className: css("icon") }),
                badge === null ? (React.createElement("span", null, children)) : (React.createElement(React.Fragment, null,
                    React.createElement("span", null, children),
                    React.createElement("span", null,
                        React.createElement(Badge_1.Badge, { variant: variant === colors_1.TColorVariants.Primary
                                ? colors_1.TColorVariants.Secondary
                                : colors_1.TColorVariants.Primary }, badge.toString()))))),
            React.createElement(Modal_1.Modal, { open: this.state.askForConfirmation, onClose: dismissConfirmation, size: dialogs_1.TDialogSizes.Medium, showCloseIcon: false },
                React.createElement(Modal_1.ModalHeader, null, (typeof shouldConfirm === "object" && shouldConfirm.title) ||
                    "Are you sure?"),
                React.createElement(Modal_1.ModalBody, null,
                    React.createElement("p", null, (typeof shouldConfirm === "object" &&
                        shouldConfirm.description) ||
                        "Please confirm that you wish to complete this action.")),
                React.createElement(Modal_1.ModalFooter, { onAccept: onButtonClick, acceptLabel: "Confirm", onCancel: dismissConfirmation, cancelLabel: "Cancel" }))));
    }
}
exports.Button = Button;
const AltButton = (props) => React.createElement(Button, Object.assign({ variant: "secondary" }, props));
exports.AltButton = AltButton;
exports.AltButton.displayName = "AltButton";
const BlockButton = (props) => React.createElement(Button, Object.assign({ isBlock: true }, props));
exports.BlockButton = BlockButton;
exports.BlockButton.displayName = "BlockButton";
//# sourceMappingURL=index.js.map